import { useEffect, useState } from "react";
import axios from "axios";
import { ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid, BarChart, Bar, Cell } from "recharts";
import { PageShell } from "@/components/mai/PageShell";
import { Eye, Users, EnvelopeSimple, PresentationChart, CursorClick, DownloadSimple, ShieldCheck } from "@phosphor-icons/react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const RANGES = [7, 30, 90];
const CONVERSIONS = {
  demo_request: ["Demo requests", EnvelopeSimple],
  deck_completed: ["Deck completed", PresentationChart],
  deck_cta_click: ["Deck CTA clicks", CursorClick],
  playbook_download: ["Playbook downloads", DownloadSimple],
};
const DEVICE_COLORS = { desktop: "#0F172A", tablet: "#047857", mobile: "#B45309", unknown: "#94A3B8" };

const Kpi = ({ icon: Icon, label, value, testid }) => (
  <div data-testid={testid} className="p-5 border border-slate-200 bg-white flex items-start gap-3">
    <Icon size={20} className="text-slate-400 mt-0.5" />
    <div>
      <p className="font-heading font-extrabold text-2xl leading-none">{value.toLocaleString()}</p>
      <p className="overline-label text-slate-400 mt-1.5">{label}</p>
    </div>
  </div>
);

const BarList = ({ title, items, labelKey, valueKey = "views", testid, empty = "No data in this range yet." }) => {
  const max = Math.max(1, ...items.map((i) => i[valueKey]));
  return (
    <section data-testid={testid} className="bg-white border border-slate-200">
      <div className="px-5 py-3.5 border-b border-slate-200"><p className="font-heading font-bold text-sm tracking-tight">{title}</p></div>
      <div className="px-5 py-3 space-y-2.5">
        {items.length === 0 && <p className="text-xs text-slate-400 py-3">{empty}</p>}
        {items.map((it) => (
          <div key={it[labelKey]} data-testid={`${testid}-row`}>
            <div className="flex justify-between text-xs mb-1">
              <span className="text-slate-700 font-medium truncate pr-3">{it[labelKey]}</span>
              <span className="font-mono-data text-slate-500 shrink-0">{it[valueKey].toLocaleString()}</span>
            </div>
            <div className="h-1.5 bg-slate-100"><div className="h-full bg-slate-900" style={{ width: `${(it[valueKey] / max) * 100}%` }} /></div>
          </div>
        ))}
      </div>
    </section>
  );
};

const fmtDay = (d) => { const [, m, day] = d.split("-"); return `${day}/${m}`; };

export default function AnalyticsPage() {
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    setError("");
    axios.get(`${API}/admin/analytics`, { params: { days } })
      .then((r) => setData(r.data))
      .catch((e) => { setData(null); setError(e.response?.status === 403 ? "Admin access required." : "Could not load analytics."); });
  }, [days]);

  const rangeSelector = (
    <div data-testid="analytics-range" className="flex border border-slate-200 bg-white">
      {RANGES.map((r) => (
        <button key={r} data-testid={`analytics-range-${r}`} onClick={() => setDays(r)}
          className={`px-3.5 py-1.5 text-xs font-semibold transition-colors ${days === r ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-100"}`}>
          {r}d
        </button>
      ))}
    </div>
  );

  return (
    <PageShell overline="Administration" title="Website Analytics" actions={rangeSelector}>
      <p data-testid="analytics-privacy-note" className="text-xs text-slate-500 flex items-center gap-2 -mt-3">
        <ShieldCheck size={14} className="text-emerald-700" /> Privacy-friendly first-party tracking — no cookies, no third parties. Admin pages excluded.
      </p>
      {error && <div data-testid="analytics-error" className="border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">{error}</div>}
      {data && (
        <>
          <div data-testid="analytics-kpis" className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-3">
            <Kpi icon={Eye} label="Page views" value={data.pageviews} testid="kpi-pageviews" />
            <Kpi icon={Users} label="Unique visitors" value={data.visitors} testid="kpi-visitors" />
            {Object.entries(CONVERSIONS).map(([k, [label, Icon]]) => (
              <Kpi key={k} icon={Icon} label={label} value={data.conversions[k] ?? 0} testid={`kpi-${k.replace(/_/g, "-")}`} />
            ))}
          </div>

          <section data-testid="analytics-series-chart" className="bg-white border border-slate-200">
            <div className="px-5 py-3.5 border-b border-slate-200 flex items-center justify-between">
              <p className="font-heading font-bold text-sm tracking-tight">Daily views vs visitors</p>
              <p className="text-[11px] text-slate-400 font-mono-data">{data.from} → {data.to}</p>
            </div>
            <div className="px-3 py-4 h-72">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={data.series} margin={{ top: 8, right: 16, left: -12, bottom: 0 }}>
                  <defs>
                    <linearGradient id="gViews" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#0F172A" stopOpacity={0.25} /><stop offset="100%" stopColor="#0F172A" stopOpacity={0} /></linearGradient>
                    <linearGradient id="gVisitors" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#047857" stopOpacity={0.35} /><stop offset="100%" stopColor="#047857" stopOpacity={0} /></linearGradient>
                  </defs>
                  <CartesianGrid stroke="#E2E8F0" vertical={false} />
                  <XAxis dataKey="day" tickFormatter={fmtDay} tick={{ fontSize: 10, fill: "#94A3B8" }} axisLine={false} tickLine={false} minTickGap={24} />
                  <YAxis allowDecimals={false} tick={{ fontSize: 10, fill: "#94A3B8" }} axisLine={false} tickLine={false} />
                  <Tooltip labelFormatter={(d) => d} contentStyle={{ fontSize: 12, border: "1px solid #E2E8F0", borderRadius: 0 }} />
                  <Area type="monotone" dataKey="views" name="Views" stroke="#0F172A" strokeWidth={2} fill="url(#gViews)" />
                  <Area type="monotone" dataKey="visitors" name="Visitors" stroke="#047857" strokeWidth={2} fill="url(#gVisitors)" />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </section>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <BarList title="Top pages" items={data.pages} labelKey="path" testid="analytics-pages" />
            <BarList title="Top sources" items={data.sources} labelKey="name" testid="analytics-sources" />
            <BarList title="Top countries" items={data.countries} labelKey="name" testid="analytics-countries" empty="No country data — requires an edge header (cf-ipcountry / x-vercel-ip-country)." />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <section data-testid="analytics-devices" className="bg-white border border-slate-200">
              <div className="px-5 py-3.5 border-b border-slate-200"><p className="font-heading font-bold text-sm tracking-tight">Devices</p></div>
              <div className="px-3 py-3 h-44">
                {data.devices.length === 0 ? <p className="text-xs text-slate-400 px-2 py-3">No data in this range yet.</p> : (
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={data.devices} margin={{ top: 8, right: 8, left: -20, bottom: 0 }}>
                      <CartesianGrid stroke="#E2E8F0" vertical={false} />
                      <XAxis dataKey="name" tick={{ fontSize: 11, fill: "#64748B" }} axisLine={false} tickLine={false} />
                      <YAxis allowDecimals={false} tick={{ fontSize: 10, fill: "#94A3B8" }} axisLine={false} tickLine={false} />
                      <Tooltip cursor={{ fill: "#F1F5F9" }} contentStyle={{ fontSize: 12, border: "1px solid #E2E8F0", borderRadius: 0 }} />
                      <Bar dataKey="views" name="Views" radius={0}>
                        {data.devices.map((d) => <Cell key={d.name} fill={DEVICE_COLORS[d.name] || "#94A3B8"} />)}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                )}
              </div>
            </section>
            <div className="lg:col-span-2">
              <BarList title="Downloads" items={data.downloads} labelKey="name" valueKey="count" testid="analytics-downloads" empty="No downloads in this range yet." />
            </div>
          </div>
        </>
      )}
    </PageShell>
  );
}
